const {
  featureCollection,
  lineString,
  point,
} = require('@turf/helpers');
const nearestPointOnLine = require('@turf/nearest-point-on-line').default;
const lineSlice = require('@turf/line-slice').default;
import along from '@turf/along';
import {getCoord} from '@turf/invariant';
import getLineLength from '@turf/length';
import partition from 'lodash/partition';
import { MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { Types } from 'mongoose';
import { useEffect } from 'react';
import { isRoutePlanningOn, LineString, setIsRoutePlanningOn } from '.';
import useMapContext from '../../../../../../hooks/useMapContext';
import { Coordinate } from '../../../../../../types/graphQLTypes';
import { SegmentWithHashKey } from '../../../../../routes/plans/route/Segment';
import { defaultGeoJsonLineString, defaultGeoJsonPoint, highlightedTrailsLayerTopId, hoveredPointLayerId, hoveredTrailsLayerId, segmentMarkerLayerId } from '../../layers';
import worker, { findRoute, WorkerMessageResponse } from './routeWorker';

interface Input {
  segments: SegmentWithHashKey[];
  onComplete: (updated: SegmentWithHashKey[]) => void;
}

const useEditPoint = ({segments, onComplete}: Input) => {
  const mapContext = useMapContext();
  useEffect(() => {

    // STATE
    let sourceSegments = [...segments];
    let targetSegmentForModification: SegmentWithHashKey | undefined;
    let targetSegmentOriginal: SegmentWithHashKey | undefined;
    let otherSegments: SegmentWithHashKey[] | undefined;
    let modifiedIndex: number | undefined;
    const potentialLines: Record<string, {
      line: LineString | undefined,
      lastResolved: boolean,
      from: Coordinate,
      to: Coordinate,
    }> = {};
    const segmentMarkerLayerSource = mapContext.intialized && (mapContext.map.getSource(segmentMarkerLayerId) as any);

    const render = () => {
      if (mapContext.intialized) {
        const allLinestrings: LineString[] = [];
        const allMarkerPoints: Record<string, any>[] = [];
        if (otherSegments && otherSegments.length) {
          otherSegments.forEach(({lines, color}) => lines.forEach(line => {
            allLinestrings.push(lineString(line, {color}));
            allMarkerPoints.push(point(line[0], {color}));
            allMarkerPoints.push(point(line[line.length - 1], {color}));
          }));
        }
        if (targetSegmentForModification && targetSegmentForModification.lines.length) {
          targetSegmentForModification.lines.forEach(line => {
            allLinestrings.push(lineString(line, {color: targetSegmentForModification?.color}));
            allMarkerPoints.push(point(line[0], {color: targetSegmentForModification?.color}));
            allMarkerPoints.push(point(line[line.length - 1], {color: targetSegmentForModification?.color}));
          });
        }

        Object.keys(potentialLines).forEach(k => {
          const thisLine = k in potentialLines && potentialLines[k] && potentialLines[k].line;
          if (thisLine) {
            allLinestrings.push(thisLine as LineString);
            const coordinates = thisLine.geometry.coordinates;
            allMarkerPoints.push(point(coordinates[0], {color: targetSegmentForModification?.color}));
            if (k === 'last-only' || k === 'b-c') {
              allMarkerPoints.push(point(coordinates[coordinates.length - 1], {color: targetSegmentForModification?.color}));
            }
          }
        });

        if (allLinestrings.length) {
          const features = allLinestrings.length > 1 ? featureCollection(allLinestrings) : allLinestrings[0];
          mapContext.setHighlightedTrails(features);
          const pointFeatures = allMarkerPoints.length > 1 ? featureCollection(allMarkerPoints) : allMarkerPoints[0];
          segmentMarkerLayerSource.setData(pointFeatures);
        } else {
          mapContext.setHighlightedTrails(defaultGeoJsonLineString);
          segmentMarkerLayerSource.setData(defaultGeoJsonPoint);
        }
      }
    };

    // EVENTS

    function onDown(e: (MapMouseEvent | MapTouchEvent) & {
      features?: mapboxgl.MapboxGeoJSONFeature[] | undefined;
      },
    ) {
      if (!isRoutePlanningOn()) {
        e.preventDefault();
        setIsRoutePlanningOn(true);
        const event = new CustomEvent('closecontextmenu');
        document.dispatchEvent(event);
        const thisPoint = e && e.features && e.features[0] ? e.features[0] : null;
        if (thisPoint && thisPoint.properties && thisPoint.properties.hashKey && mapContext.intialized) {
          clearHoveredItems();
          if (thisPoint.layer.id === highlightedTrailsLayerTopId) {
            sourceSegments = sourceSegments.map(segment => {
              if (segment.hashKey === thisPoint?.properties?.hashKey) {
                const newLines: Coordinate[][] = [];
                let newIndex = thisPoint.properties.index;
                for (let i = 0; i < segment.lines.length; i++) {
                  if (i === thisPoint.properties.index) {
                    const midPoint = nearestPointOnLine(
                      thisPoint.geometry,
                      point([e.lngLat.lng, e.lngLat.lat]),
                      {units: 'miles'},
                    );
                    const fullLinestring = lineString(segment.lines[i], segment.lines[i]);
                    const startPoint = segment.lines[i][0];
                    const endPoint = segment.lines[i][segment.lines[i].length - 1];
                    const lineFirstHalf = lineSlice(startPoint, midPoint, fullLinestring);
                    const lineSecondHalf = lineSlice(midPoint, endPoint, fullLinestring);
                    newLines.push(
                      lineFirstHalf.geometry.coordinates,
                      lineSecondHalf.geometry.coordinates,
                    );
                    newIndex = i + 1;
                  } else {
                    newLines.push(segment.lines[i]);
                  }
                }
                thisPoint.properties.index = newIndex;
                return {
                  ...segment,
                  lines: newLines,
                };
              } else {
                return segment;
              }
            });
          }
          const {hashKey, index} = thisPoint.properties;
          const [target, other] = partition(sourceSegments, s => s.hashKey === hashKey);
          if (other && other.length) {
            otherSegments = other;
          }
          if (target && target[0]) {
            modifiedIndex = index;
            targetSegmentForModification = target[0];
            targetSegmentOriginal = {
              name: target[0].name,
              color: target[0].color,
              lines: target[0].lines.map(line => line),
              trails: target[0].trails,
              hashKey: target[0].hashKey,
            };
            if (index === 0) {
              // first line, first point in segment
              // should only find route for first line in segment
              // from = mousepoint
              // to = firstLine[firstLine.length - 1]
              const thisLine = targetSegmentForModification.lines.shift();
              if (thisLine) {
                potentialLines['first-only'] = {
                  line: lineString(thisLine, {color: targetSegmentOriginal.color}),
                  lastResolved: true,
                  from: thisLine[0],
                  to: thisLine[thisLine.length - 1],
                };
              }
            } else if (index === targetSegmentForModification.lines.length) {
              // last line, last point in segment
              // should only find route for final line
              // from = lastLine[0]
              // to = mousepoint
              const thisLine = targetSegmentForModification.lines.pop();
              if (thisLine) {
                potentialLines['last-only'] = {
                  line: lineString(thisLine, {color: targetSegmentOriginal.color}),
                  lastResolved: true,
                  from: thisLine[0],
                  to: thisLine[thisLine.length - 1],
                };
              }
            } else {
              // point is somewhere in between
              // should find 2 routes
              // from1 = last point of line[index - 1]
              // to1 = mousepoint
              // from2 = mousepoint
              // to2 = last point of line[index]
              let partitionIndex = -1;
              const [theseLines, otherLines] = partition(targetSegmentForModification.lines, () => {
                partitionIndex++;
                return partitionIndex === index || partitionIndex === index - 1;
              });
              targetSegmentForModification.lines = otherLines;
              if (theseLines) {
                potentialLines['a-b'] = {
                  line: lineString(theseLines[0], {color: targetSegmentOriginal.color}),
                  lastResolved: true,
                  from: theseLines[0][0],
                  to: theseLines[0][theseLines[0].length - 1],
                };
                potentialLines['b-c'] = {
                  line: lineString(theseLines[1], {color: targetSegmentOriginal.color}),
                  lastResolved: true,
                  from: theseLines[1][0],
                  to: theseLines[1][theseLines[1].length - 1],
                };
              }
            }
          }
          const hoveredTrailSource = mapContext.map.getSource(hoveredTrailsLayerId) as any;
          if (hoveredTrailSource) {
            hoveredTrailSource.setData(defaultGeoJsonLineString);
          }
          mapContext.map.on('mousemove', onMove);
          mapContext.map.on('touchmove', onMove);
          mapContext.map.on('touchend', cleanup);
          mapContext.map.on('mouseup', cleanup);
          worker.addEventListener('message', onWorkerMessage);
          render();
        }
      }
    }

    function onMove(e: MapMouseEvent & {
      features?: mapboxgl.MapboxGeoJSONFeature[] | undefined;
      },
    ) {
      const mouseCoordinate: Coordinate = [e.lngLat.lng, e.lngLat.lat];
      Object.keys(potentialLines).forEach(key => {
        const {from, to, lastResolved} = potentialLines[key];
        if (lastResolved) {
          if (key === 'first-only') {
            // from = mousepoint
            // to = firstLine[firstLine.length - 1]
            findRoute({from: mouseCoordinate, to, networkCenter: mouseCoordinate, key });
            potentialLines[key].from = mouseCoordinate;
            potentialLines[key].lastResolved = false;
          } else if (key === 'last-only') {
            // from = lastLine[0]
            // to = mousepoint
            findRoute({from, to: mouseCoordinate, networkCenter: mouseCoordinate, key });
            potentialLines[key].to = mouseCoordinate;
            potentialLines[key].lastResolved = false;
          } else if (key === 'a-b') {
            // from1 = last point of line[index - 1]
            // to1 = mousepoint
            findRoute({from, to: mouseCoordinate, networkCenter: mouseCoordinate, key });
            potentialLines[key].to = mouseCoordinate;
            potentialLines[key].lastResolved = false;
          } else if (key === 'b-c') {
            // from2 = mousepoint
            // to2 = last point of line[index]
            findRoute({from: mouseCoordinate, to, networkCenter: mouseCoordinate, key });
            potentialLines[key].from = mouseCoordinate;
            potentialLines[key].lastResolved = false;
          }
        }
      });
    }

    // Change the cursor to a pointer when the mouse is over the places layer.
    function onLineHover(e: MapMouseEvent & {
      features?: mapboxgl.MapboxGeoJSONFeature[] | undefined;
      },
    ) {
      if (!isRoutePlanningOn() && mapContext.intialized) {
        if (e && e.features && e.features[0] && e.features[0].geometry) {
          mapContext.map.getCanvas().style.cursor = 'pointer';
          const hoveredPointSource = mapContext.map.getSource(hoveredPointLayerId) as any;
          if (hoveredPointSource) {
            const truePoint = nearestPointOnLine(
              e.features[0].geometry,
              point([e.lngLat.lng, e.lngLat.lat]),
              {units: 'miles'},
            );
            if (truePoint) {
              truePoint.properties = e.features[0].properties;
              hoveredPointSource.setData(truePoint);
            }
          }
        }
      }
    }

    function clearHoveredItems() {
      if (mapContext.intialized) {
        mapContext.map.getCanvas().style.cursor = '';
        mapContext.clearHoveredPoints();
        const hoveredLinesSource = mapContext.map.getSource(hoveredTrailsLayerId) as any;
        if (hoveredLinesSource) {
          hoveredLinesSource.setData(defaultGeoJsonLineString);
        }
      }
    }

    // Change it back to a pointer when it leaves.
    function onLineHoverLeave() {
      if (!isRoutePlanningOn()) {
        clearHoveredItems();
      }
    }

    function cleanup() {
      if (targetSegmentOriginal) {
        const updatedSegment = {...targetSegmentOriginal};
        updatedSegment.hashKey = new Types.ObjectId().toString();
        Object.keys(potentialLines).forEach(key => {
          if (key === 'first-only' && potentialLines['first-only'].line) {
            updatedSegment.lines[0] = potentialLines['first-only'].line.geometry.coordinates;
          } else if (key === 'last-only' && potentialLines['last-only'].line) {
            updatedSegment.lines[updatedSegment.lines.length - 1] = potentialLines['last-only'].line.geometry.coordinates;
          } else if (key === 'a-b' && potentialLines['a-b'].line && modifiedIndex !== undefined) {
            updatedSegment.lines[modifiedIndex - 1] = potentialLines['a-b'].line.geometry.coordinates;
          } else if (key === 'b-c' && potentialLines['b-c'].line && modifiedIndex !== undefined) {
            updatedSegment.lines[modifiedIndex] = potentialLines['b-c'].line.geometry.coordinates;
          }
        });
        const indexOfTarget = sourceSegments.findIndex(d => d.hashKey === targetSegmentOriginal?.hashKey);
        const updatedSegments = [...sourceSegments];
        if (indexOfTarget !== -1) {
          updatedSegments[indexOfTarget] = updatedSegment;
        }
        onComplete(updatedSegments);
      }
      setIsRoutePlanningOn(false);
      worker.removeEventListener('message', onWorkerMessage);
      if (mapContext.intialized) {
        mapContext.map.off('touchmove', onMove);
        mapContext.map.off('mousemove', onMove);
        mapContext.map.off('touchend', cleanup);
        mapContext.map.off('mouseup', cleanup);
      }
    }

    const onWorkerMessage = (event: MessageEvent<WorkerMessageResponse>) => {
      let newPotentialLine: LineString | undefined;
      const key = event && event.data && event.data.key && event.data.key in potentialLines
        ? event.data.key as 'first-only' | 'last-only' | 'a-b' | 'b-c'
        : undefined;
      if (event && event.data && event.data.line && key !== undefined) {
        potentialLines[key].lastResolved = true;
        newPotentialLine = event.data.line;
        newPotentialLine.properties.color = targetSegmentOriginal?.color;
      } else if (event && event.data.error && key !== undefined && potentialLines[key] !== undefined) {
        const {from, to} = potentialLines[key];
        if (from && to) {
          const pointToPointLine = lineString([from, to], {color: targetSegmentOriginal?.color});
          const length = getLineLength(pointToPointLine);
          let dist = 0;
          const lineWithAddedPoints: Coordinate[] = [];
          while (dist < length) {
            const newPoint = along(pointToPointLine, dist, {units: 'miles'});
            if (newPoint) {
              lineWithAddedPoints.push(getCoord(newPoint) as Coordinate);
            }
            dist = dist + 0.01;
          }
          if (lineWithAddedPoints.length > 1) {
            newPotentialLine = lineString(lineWithAddedPoints, {color: targetSegmentOriginal?.color});
          } else {
            newPotentialLine = pointToPointLine;
          }
        }
        potentialLines[key].lastResolved = true;
      }
      if (newPotentialLine && key && potentialLines[key]) {
        potentialLines[key].line = newPotentialLine;
        render();
      }
    };

    // INITIALIZE
    if (mapContext.intialized) {
      mapContext.map.on('mousedown', segmentMarkerLayerId, onDown);
      mapContext.map.on('touchstart', segmentMarkerLayerId, onDown);
      mapContext.map.on('mousemove', highlightedTrailsLayerTopId, onLineHover);
      mapContext.map.on('mousedown', highlightedTrailsLayerTopId, onDown);
      mapContext.map.on('mouseleave', highlightedTrailsLayerTopId, onLineHoverLeave);
    }
    // CLEANUP BETWEEN RENDERS
    return () => {
      cleanup();
      if (mapContext.intialized) {
        mapContext.map.off('mousedown', segmentMarkerLayerId, onDown);
        mapContext.map.off('touchstart', segmentMarkerLayerId, onDown);
        mapContext.map.off('mousemove', highlightedTrailsLayerTopId, onLineHover);
        mapContext.map.off('mousedown', highlightedTrailsLayerTopId, onDown);
        mapContext.map.off('mouseleave', highlightedTrailsLayerTopId, onLineHoverLeave);
      }
    };
  }, [mapContext, segments, onComplete]);
};

export default useEditPoint;
